import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { getHero, updateHero, uploadHeroImage } from '../../services/api';
import '../../styles/Admin.css';

const EditHero = () => {
  const [formData, setFormData] = useState({
    title: '',
    subtitle: '',
    cta_text: '',
    background_image: null
  });
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      navigate('/admin/login');
      return;
    }

    fetchHeroData();
  }, [navigate]);

  const fetchHeroData = async () => {
    try {
      const response = await getHero();
      const data = response.data || response;
      if (data) {
        setFormData({
          title: data.title || '',
          subtitle: data.subtitle || '',
          cta_text: data.cta_text || '',
          background_image: data.background_image || null
        });
        if (data.background_image) {
          setImagePreview(`${process.env.REACT_APP_API_URL}${data.background_image}`);
        }
      }
    } catch (error) {
      console.error('Error fetching hero data:', error);
      setMessage({ type: 'error', text: 'فشل في تحميل البيانات' });
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
    // مسح الرسالة عند التعديل
    if (message.text) {
      setMessage({ type: '', text: '' });
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // التحقق من نوع الملف
      if (!file.type.startsWith('image/')) {
        setMessage({ type: 'error', text: 'يرجى اختيار ملف صورة' });
        return;
      }

      // التحقق من حجم الملف (25MB)
      if (file.size > 25 * 1024 * 1024) {
        setMessage({ type: 'error', text: 'يجب أن يكون حجم الصورة أقل من 25 ميجابايت' });
        return;
      }

      setImageFile(file);
      
      // عرض معاينة الصورة
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleUploadImage = async () => {
    if (!imageFile) {
      setMessage({ type: 'error', text: 'يرجى اختيار صورة أولاً' });
      return;
    }

    setUploadingImage(true);
    setMessage({ type: '', text: '' });

    try {
      const response = await uploadHeroImage(imageFile);
      setFormData({
        ...formData,
        background_image: response.imageUrl
      });
      setMessage({ type: 'success', text: 'تم رفع الصورة بنجاح!' });
      setImageFile(null);
    } catch (error) {
      console.error('Error uploading image:', error);
      setMessage({ type: 'error', text: 'فشل في رفع الصورة' });
    } finally {
      setUploadingImage(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // التحقق من الحقول
    if (!formData.title || !formData.subtitle || !formData.cta_text) {
      setMessage({ type: 'error', text: 'يرجى ملء جميع الحقول' });
      return;
    }

    setSaving(true);
    setMessage({ type: '', text: '' });

    try {
      await updateHero(formData);
      setMessage({ type: 'success', text: 'تم تحديث قسم الرئيسي بنجاح!' });
    } catch (error) {
      console.error('Error updating hero:', error);
      setMessage({ type: 'error', text: 'فشل في تحديث قسم الرئيسي' });
    } finally {
      setSaving(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    navigate('/admin/login');
  };

  if (loading) {
    return (
      <div className="admin-loading">
        <div className="spinner"></div>
        <p>جاري التحميل...</p>
      </div>
    );
  }

  return (
    <div className="admin-layout" dir="rtl">
      {/* Mobile Menu Button */}
      <button 
        className={`mobile-menu-btn ${sidebarOpen ? 'active' : ''}`}
        onClick={() => setSidebarOpen(!sidebarOpen)}
      >
        <span></span>
        <span></span>
        <span></span>
      </button>

      {/* Sidebar Overlay */}
      <div 
        className={`sidebar-overlay ${sidebarOpen ? 'active' : ''}`}
        onClick={() => setSidebarOpen(false)}
      />

      {/* Sidebar */}
      <aside className={`admin-sidebar ${sidebarOpen ? 'open' : ''}`}>
        <div className="admin-sidebar-header">
          <h2 className="admin-sidebar-logo">لوحة التحكم</h2>
          <p className="admin-sidebar-subtitle">إدارة المعرض</p>
        </div>

        <nav>
          <ul className="admin-nav">
            <li className="admin-nav-item">
              <Link to="/admin/dashboard" className="admin-nav-link" onClick={() => setSidebarOpen(false)}>
                <span className="nav-icon">📊</span>
                <span>الرئيسية</span>
              </Link>
            </li>
            <li className="admin-nav-item">
              <Link to="/admin/hero" className="admin-nav-link active" onClick={() => setSidebarOpen(false)}>
                <span className="nav-icon">🎯</span>
                <span>قسم الرئيسي</span>
              </Link>
            </li>
            <li className="admin-nav-item">
              <Link to="/admin/about" className="admin-nav-link" onClick={() => setSidebarOpen(false)}>
                <span className="nav-icon">👤</span>
                <span>نبذة عني</span>
              </Link>
            </li>
            <li className="admin-nav-item">
              <Link to="/admin/projects" className="admin-nav-link" onClick={() => setSidebarOpen(false)}>
                <span className="nav-icon">🏗️</span>
                <span>المشاريع</span>
              </Link>
            </li>
            <li className="admin-nav-item">
              <Link to="/admin/contact" className="admin-nav-link" onClick={() => setSidebarOpen(false)}>
                <span className="nav-icon">📧</span>
                <span>معلومات التواصل</span>
              </Link>
            </li>
            <li className="admin-nav-item">
              <Link to="/admin/change-password" className="admin-nav-link" onClick={() => setSidebarOpen(false)}>
                <span className="nav-icon">🔒</span>
                <span>تغيير كلمة المرور</span>
              </Link>
            </li>
            <li className="admin-nav-item">
              <a href="/" className="admin-nav-link" target="_blank" rel="noopener noreferrer">
                <span className="nav-icon">🌐</span>
                <span>عرض الموقع</span>
              </a>
            </li>
          </ul>
        </nav>

        <div className="admin-sidebar-footer">
          <button className="logout-btn" onClick={handleLogout}>
            <span>🚪</span>
            تسجيل الخروج
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="admin-main">
        <div className="admin-header">
          <h1>تعديل قسم الرئيسي</h1>
          <p>تحديث البانر الرئيسي للصفحة الرئيسية</p>
        </div>

        <div className="admin-content">
          {message.text && (
            <div className={`alert alert-${message.type}`}>
              {message.text}
            </div>
          )}

          <form onSubmit={handleSubmit} className="admin-form">
            {/* Title */}
            <div className="form-group">
              <label htmlFor="title">
                العنوان الرئيسي *
                <span className="field-hint">سيكون هذا العنوان الرئيسي</span>
              </label>
              <input
                type="text"
                id="title"
                name="title"
                value={formData.title}
                onChange={handleChange}
                placeholder="مثال: التميز المعماري"
                disabled={saving}
                required
              />
            </div>

            {/* Subtitle */}
            <div className="form-group">
              <label htmlFor="subtitle">
                العنوان الفرعي *
                <span className="field-hint">النص الداعم تحت العنوان</span>
              </label>
              <textarea
                id="subtitle"
                name="subtitle"
                value={formData.subtitle}
                onChange={handleChange}
                placeholder="مثال: إنشاء مساحات تلهم وتدوم"
                rows="3"
                disabled={saving}
                required
              />
            </div>

            {/* CTA Text */}
            <div className="form-group">
              <label htmlFor="cta_text">
                نص الزر *
                <span className="field-hint">نص زر الدعوة للإجراء</span>
              </label>
              <input
                type="text"
                id="cta_text"
                name="cta_text"
                value={formData.cta_text}
                onChange={handleChange}
                placeholder="مثال: عرض المشاريع"
                disabled={saving}
                required
              />
            </div>

            {/* Background Image */}
            <div className="form-group">
              <label>
                صورة الخلفية
                <span className="field-hint">رفع صورة عالية الجودة (الحد الأقصى 25 ميجابايت)</span>
              </label>

              {imagePreview && (
                <div className="image-preview">
                  <img src={imagePreview} alt="خلفية الرئيسي" />
                </div>
              )}

              <div className="file-upload-wrapper">
                <input
                  type="file"
                  id="hero-image"
                  accept="image/*"
                  onChange={handleImageChange}
                  disabled={uploadingImage}
                />
                <label htmlFor="hero-image" className="file-upload-label">
                  <span className="upload-icon">📁</span>
                  {imageFile ? imageFile.name : 'اختر صورة'}
                </label>
              </div>

              {imageFile && (
                <button
                  type="button"
                  onClick={handleUploadImage}
                  className="btn-upload"
                  disabled={uploadingImage}
                >
                  {uploadingImage ? (
                    <>
                      <span className="spinner-small"></span>
                      جاري الرفع...
                    </>
                  ) : (
                    'رفع الصورة'
                  )}
                </button>
              )}
            </div>

            {/* Preview Section */}
            <div className="preview-section">
              <h3>معاينة</h3>
              <div className="hero-preview">
                <h1>{formData.title || 'عنوانك هنا'}</h1>
                <p>{formData.subtitle || 'سيظهر العنوان الفرعي هنا'}</p>
                <button className="preview-btn">
                  {formData.cta_text || 'نص الزر'}
                </button>
              </div>
            </div>

            {/* Submit Buttons */}
            <div className="form-actions">
              <button
                type="submit"
                className="btn-save"
                disabled={saving}
              >
                {saving ? (
                  <>
                    <span className="spinner-small"></span>
                    جاري الحفظ...
                  </>
                ) : (
                  <>
                    <span>💾</span>
                    حفظ التغييرات
                  </>
                )}
              </button>

              <Link to="/admin/dashboard" className="btn-cancel">
                إلغاء
              </Link>
            </div>
          </form>
        </div>
      </main>
    </div>
  );
};

export default EditHero;